import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getStoredUser, logout } from '@/hooks/useAuth';
import { api, authFetch, API_BASE_URL } from '@/services/api';
import { useSelectedAsset, SelectedAsset } from '@/hooks/useSelectedAsset';
import { ASSET_TO_MOCK, scadaStationIdForAsset } from '@/scada/useScopedStations';
import { parseStationPath, stationPath } from '@/scada/navConfig';
import { useTheme } from '@/hooks/useTheme';
import { useForecastMmsDate, getFcmmsDayTabs, shiftDay } from '@/hooks/useForecastMmsDate';
import { useFinancialResultUi, shiftMonth } from '@/hooks/useFinancialResultUi';
import { useForecastUi, shiftDayIso } from '@/hooks/useForecastUi';
import { useScheduleDraftUi, shiftDraftDay } from '@/hooks/useScheduleDraftUi';
import { useMarketPricesUi, shiftDayIso as shiftMkDay, type MarketTab, type MarketView } from '@/hooks/useMarketPricesUi';
import { useTradingUi, tradingTodayIso, type TradingDays } from '@/hooks/useTradingUi';
import { useComdispUi, shiftComdispDay } from '@/hooks/useComdispUi';
import {
  useBudgetUi, BUDGET_TABS, BUDGET_SCENARIOS, BUDGET_STAGES,
  type BudgetTab, type BudgetScenario, type BudgetStage,
} from '@/hooks/useBudgetUi';
import { useSkoUi, type SkoSection } from '@/hooks/useSkoUi';
import { useSaleTermsUi } from '@/hooks/useSaleTermsUi';
import { STATUSES as SALE_TERM_STATUSES } from '@/pages/financial/theme';
import TokenSelect from '@/components/base/TokenSelect';
import { MonthField } from '@/components/base/UaCalendar';
import { useAssetsUi, ASSET_TYPES } from '@/hooks/useAssetsUi';
import { useUeexUi, UEEX_DAYS, type UeexSection } from '@/hooks/useUeexUi';
import { useContractsUi } from '@/hooks/useContractsUi';
import { useUsersUi } from '@/hooks/useUsersUi';
import { useDocsUi, docsTabNeedsStation } from '@/hooks/useDocsUi';
import SegmentedTabs from '@/components/base/SegmentedTabs';
import { getDayTabLabels } from '@/mocks/generationForecast';
import { localizeRegion } from '@/utils/region';
import {
  Calculator, Calendar, ChevronLeft, ChevronRight, FileSpreadsheet, Search,
  CalendarDays, CalendarRange, Target, Wallet, PencilLine, FileCheck,
  TrendingUp, TrendingDown, Minus, type LucideIcon,
} from 'lucide-react';
import { hasArtemKView } from '@/utils/artemkView';

interface TopbarProps {
  sidebarWidth: number;
  breadcrumb?: string[];
  language: string;
  onLanguageChange: (lang: string) => void;
  onMobileMenuToggle: () => void;
  isMobile: boolean;
}

// Найдовша назва розділу СЕРЕД ТИХ, де показується селектор станції
// (умова hideStationSelector нижче). Станом на зараз це вкладка SCADA
// «Моніторинг обладнання» — 21 символ; наступна за нею «Фінансовий
// результат». Рядок вживається лише як невидимий вимірювач ширини зони
// заголовка: якщо колись зʼявиться довша назва, її треба вписати сюди,
// інакше вона просто обріжеться трикрапкою.
const LONGEST_SECTION_TITLE = 'Моніторинг обладнання';

interface AssetItem {
  id: string;
  assetName: string;
  installedCapacityMw: number;
  region: string;
  operationalStatus: string;
  counterpartyShortName?: string;
  counterpartyName?: string;
}

/**
 * Кольори контролів хедера. Винесені з тіла Topbar, бо ними користуються ще й
 * дрібні контроли нижче — інакше кожен тримав би власну копію тих самих
 * значень, і теми роз'їхалися б при першій правці.
 */
const hdrColors = (isDark: boolean) => ({
  textMain: isDark ? '#E0E0E0' : '#1A1A1A',
  textMuted: '#6B6B70',
  inputBg: isDark ? 'rgba(255,255,255,0.05)' : '#F5F5F7',
  inputBorder: isDark ? 'rgba(255,255,255,0.1)' : '#E5E5E8',
  ctrlSh: isDark ? '0 2px 8px rgba(0,0,0,0.28)' : '0 2px 8px rgba(40,50,60,0.10)',
});

/* ── контроли дати хедера ─────────────────────────────────────────────────────
   ОДИН ПОРЯДОК НА ВСІ РОЗДІЛИ: поле календаря, праворуч — крок назад і крок
   вперед. Так само, як у блоці періоду на «Головній». Раніше кожен розділ
   збирав трійку ◀ дата ▶ власною розміткою — сім копій тих самих стилів, і
   стрілки стояли по обидва боки поля.

   ШИРИНА ПОЛЯ СТАЛА, не за вмістом: при зміні формату (дата ↔ місяць) або
   довжини підпису кнопки поруч стрибали. Геометрія хедерна — 36 / radius 10 /
   тінь (§6). */

/** Поле вибору дати. Календар відкриває наш український пікер (UaCalendar). */
function HdrDateField({ value, min, max, onChange, title, width = 150 }: {
  value: string;
  min?: string;
  max?: string;
  onChange: (v: string) => void;
  title?: string;
  width?: number;
}) {
  const { isDark } = useTheme();
  const c = hdrColors(isDark);
  return (
    <div
      className="flex items-center shrink-0"
      title={title}
      style={{
        gap: 8, height: 36, width, padding: '0 10px', borderRadius: 10,
        background: c.inputBg, border: `1px solid ${c.inputBorder}`, boxShadow: c.ctrlSh,
      }}
    >
      <Calendar size={14} strokeWidth={2} style={{ color: c.textMuted, flexShrink: 0 }} />
      <input
        type="date"
        value={value}
        min={min}
        max={max}
        onChange={(e) => { if (e.target.value) onChange(e.target.value); }}
        className="outline-none cursor-pointer tabular-nums w-full"
        style={{
          background: 'transparent', border: 'none', color: c.textMain,
          fontSize: 13, fontWeight: 700, colorScheme: isDark ? 'dark' : 'light',
        }}
      />
    </div>
  );
}

/**
 * Поле вибору місяця. Підпис українською малює MonthField: нативний
 * `type=month` показує назву місяця мовою інтерфейсу браузера.
 *
 * 180 — під найдовші підписи («Вересень 2026», «Листопад 2026»); на меншій
 * ширині вони ламались на два рядки.
 */
function HdrMonthField({ value, min, max, onChange, width = 180 }: {
  value: string;
  min?: string;
  max?: string;
  onChange: (ym: string) => void;
  width?: number;
}) {
  const { isDark } = useTheme();
  const c = hdrColors(isDark);
  return (
    <MonthField
      value={value}
      min={min}
      max={max}
      onChange={onChange}
      width={width}
      style={{
        height: 36, borderRadius: 10, padding: '0 10px',
        background: c.inputBg, border: `1px solid ${c.inputBorder}`, boxShadow: c.ctrlSh,
        color: c.textMain, fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap',
      }}
    />
  );
}

/** Крок періоду — квадратна кнопка 36×36 праворуч від поля. */
function HdrStep({ dir, onClick, disabled, title }: {
  dir: 'prev' | 'next';
  onClick: () => void;
  disabled?: boolean;
  title: string;
}) {
  const { isDark } = useTheme();
  const c = hdrColors(isDark);
  const Icon = dir === 'prev' ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      onClick={() => { if (!disabled) onClick(); }}
      disabled={disabled}
      className="flex items-center justify-center shrink-0"
      title={title}
      aria-label={title}
      style={{
        width: 36, height: 36, borderRadius: 10,
        background: c.inputBg, border: `1px solid ${c.inputBorder}`, boxShadow: c.ctrlSh,
        color: c.textMain, opacity: disabled ? 0.5 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      <Icon size={17} strokeWidth={2} />
    </button>
  );
}

export default function Topbar({ sidebarWidth, breadcrumb = [], language, onLanguageChange, onMobileMenuToggle, isMobile }: TopbarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  // Виклик стоїть ДО маршрутних прапорців, бо isDocsStationTab читає dc.tab
  // одразу нижче, а решта UI-хуків оголошена значно пізніше за них.
  const dc = useDocsUi();
  // Прогноз/MMS переносить у топбар свій заголовок, станцію та керування добою,
  // щоб вони лишались на виду при скролі. Усе під цим прапорцем — інші маршрути
  // топбара лишаються незмінними.
  //
  // Гейт той самий, що й на пункт меню (Sidebar): додатково вимагаємо
  // VITE_FEATURE_FORECAST_MMS === '1'. У прод-збірці цього прапорця немає
  // (.env — skip-worktree), тож isFcmms там завжди false і топбар лишається
  // стандартним навіть при прямому переході за URL. Зміни шапки діють
  // ВИКЛЮЧНО на тестовому стенді cabinet2.
  const isFcmms = location.pathname === '/dispatch/forecast-mms'
    && import.meta.env.VITE_FEATURE_FORECAST_MMS === '1';
  // Той самий прийом для «Фінансового результату»: шапка (заголовок, W-код,
  // станція, період, дата, «Факт. розрахунок») переноситься в топбар. Гейт —
  // прапорець стенду; у проді isFinResult завжди false.
  const isFinResult = location.pathname === '/financial/result'
    && import.meta.env.VITE_FEATURE_FINANCIAL_RESULT === '1';
  // /forecast (редизайн) теж переносить керування добою + експорт у топбар.
  const isForecastRd = location.pathname === '/forecast'
    && import.meta.env.VITE_REDESIGN === '1';
  // /market/prices (редизайн): вкладка ринку · дата · Графік/Таблиця у топбарі.
  const isMarketPrices = location.pathname === '/market/prices'
    && import.meta.env.VITE_REDESIGN === '1';
  // «Бюджет»: вкладка · місяць · сценарій цін у топбарі. Прапорцем редизайну
  // НЕ гейтимо, на відміну від сусідів: у цього розділу немає старої версії,
  // і без топбара сторінка лишилась би без перемикача вкладок.
  const isBudget = location.pathname === '/budget';
  // «Торгівля» (редизайн): глибина історії 7/14/30/60 діб + доба погодинного
  // профілю у топбарі. Один прапорець на всі три маршрути ринку.
  const isTrading = ['/trading', '/trading/dam', '/trading/idm'].includes(location.pathname)
    && import.meta.env.VITE_REDESIGN === '1';
  // «Передача до MMS» (редизайн): доба поставки в топбарі. Селектор станції
  // тут ПОТРІБЕН (сторінка працює по одній СЕС), тож у hideStationSelector
  // не додається — його показує сам факт takeover.
  const isComdisp = location.pathname === '/dashboard/owner/comdisp'
    && import.meta.env.VITE_REDESIGN === '1';
  // «Команди диспетчера» (редизайн): перемикач секцій у топбарі замість
  // самописної смуги вкладок у тілі сторінки.
  const isSko = location.pathname === '/sko' && import.meta.env.VITE_REDESIGN === '1';
  // МАКЕТ торгового графіка: доба подачі (D+1 / D+2 · дата · ◀ ▶) у топбарі,
  // як на «Прогнозі» й «Цінах». Селектор станції приховано — сторінка
  // демонстраційна й жорстко прив'язана до однієї СЕС.
  const isScheduleDraft = location.pathname === '/dispatch/schedule-draft';
  // «Довідник умов» (редизайн): фільтри об'єкта і статусу в топбарі.
  // Розділ уже під гейтом VITE_FEATURE_FINANCIAL_RESULT, тож у проді його немає.
  const isSaleTerms = location.pathname === '/financial/sale-terms'
    && import.meta.env.VITE_REDESIGN === '1';
  // «Перелік СЕС» (редизайн): пошук і фільтр типу в топбарі. Селектор
  // станції тут зайвий — сторінка і є переліком усіх станцій.
  const isAssets = location.pathname === '/assets' && import.meta.env.VITE_REDESIGN === '1';
  // Картка активу /assets/:id працює по ОДНІЙ СЕС, тож селектор тут потрібен —
  // так само, як на «Прогнозі» й у SCADA. У переліку /assets він навпаки
  // зайвий (сторінка і є переліком усіх станцій), тому isAssets лишається в
  // hideStationSelector. Ідентифікатор у маршруті — це uuid активу з БД,
  // тобто те саме, що selectedAsset.id: додаткового зіставлення не треба.
  const assetDetailId = /^\/assets\/([^/]+)\/?$/.exec(location.pathname)?.[1] ?? null;
  const isAssetDetail = !!assetDetailId && import.meta.env.VITE_REDESIGN === '1';
  // Ринок (УЕБ, редизайн): секції та глибина вікна в топбарі замість
  // власної правої колонки-сайдбара в тілі сторінки.
  const isUeex = location.pathname === '/market/ueex' && import.meta.env.VITE_REDESIGN === '1';
  // Договори (редизайн): пошук у топбарі. Селектор станції тут зайвий —
  // реєстр охоплює всі активи одразу.
  const isContracts = location.pathname === '/contracts' && import.meta.env.VITE_REDESIGN === '1';
  // Користувачі (редизайн): вибір тенанта й пошук у топбарі замість
  // власної лівої колонки в тілі сторінки.
  const isUsers = location.pathname === '/admin/users' && import.meta.env.VITE_REDESIGN === '1';
  // «Документи» (/docs): селектор СЕС потрібен НЕ на всій сторінці, а лише на
  // трьох вкладках із чотирьох. Договори, звітність і технічна документація
  // прив'язані до конкретного об'єкта; нормативні акти — ні, закони однакові
  // для всього портфелю, і селектор там був би контролом, що нічого не
  // змінює. Активну вкладку топбар дізнається з useDocsUi.
  //
  // Повного takeover не вмикаємо (як і на SCADA-моніторингу): пошук, СКО-бейдж
  // і сповіщення в шапці мають лишитись — сторінка довідкова, а не операційна.
  // У кабінеті ArtemK «Торговий графік» — не окремий розділ, а четверта
  // вкладка «Цін». Решта акаунтів цього не бачить.
  const isArtemK = hasArtemKView(getStoredUser()?.login);
  const isDocs = location.pathname === '/docs' && import.meta.env.VITE_REDESIGN === '1';
  const isDocsStationTab = isDocs && docsTabNeedsStation(dc.tab);
  // SCADA «Моніторинг обладнання»: сторінці потрібен селектор СЕС у тому ж
  // вигляді, що й на «Прогнозі». Повного takeover НЕ вмикаємо — у диспетчера
  // мають лишитись СКО-бейдж і сповіщення в топбарі.
  // Той самий селектор працює і на картці станції /scada/station/:id, куди
  // тепер веде пункт меню: там він і показує поточну СЕС, і перемикає її.
  const scadaRoute = parseStationPath(location.pathname);
  const scadaStationId = scadaRoute?.id ?? null;
  // Селектор показуємо на ВСІХ сторінках SCADA: станцію має бути видно й можна
  // змінити будь-коли, зокрема з «Ручного керування» чи «Подій». Сам вибір
  // зберігається в useSelectedAsset — єдиному сховищі станції в кабінеті.
  const isScadaMon = location.pathname.startsWith('/scada/')
    && import.meta.env.VITE_REDESIGN === '1';
  // Спільна для «takeover»-сторінок структура топбара (без пошуку, мова/тема
  // праворуч, компактний аватар). Центр лишається специфічним для кожної.
  const isTakeover = isFcmms || isFinResult || isForecastRd || isMarketPrices || isBudget || isTrading || isComdisp || isSko || isSaleTerms || isAssets || isUeex || isContracts || isUsers || isScheduleDraft;
  // The station selector is only meaningful on /forecast (switch the analysed SES)
  // and on Прогноз/MMS / Фінансовий результат. It is hidden on every other page —
  // зокрема на «Ринок, ціни» (ціни ринкові, не по станції), попри takeover.
  // Ціни й торги — ринкові, не по станції: селектор станції там зайвий.
  const hideStationSelector = (!location.pathname.startsWith('/forecast') && !isTakeover && !isScadaMon && !isAssetDetail && !isDocsStationTab) || isMarketPrices || isTrading || isSko || isSaleTerms || isAssets || isUeex || isContracts || isUsers || isScheduleDraft;
  // Вигляд самого селектора: заповнена пілюля 36/10 з тінню. Вмикається на
  // takeover-сторінках і на SCADA-моніторингу — вигляд один, структура
  // топбара при цьому лишається різною.
  const stationPill = isTakeover || isScadaMon || isAssetDetail || isDocsStationTab;
  // Route -> header breadcrumb. Falls back to the page-provided `breadcrumb` prop.
  const ROUTE_CRUMBS: Record<string, string[]> = {
    '/dashboard/owner': [t('d_bc_home'), t('nav_owner_home')],
    '/forecast': [t('d_bc_forecast')],
    // Назва береться з того самого ключа, що й пункт меню (nav_forecast_mms
    // = «Прогноз/MMS»): був зашитий верхній регістр, який розходився з
    // сайдбаром і не перекладався.
    // ArtemK бачить цей розділ як «Оптимізація» — заголовок сторінки має
    // збігатися з підписом у його меню. Для решти акаунтів — «Прогноз/MMS».
    '/dispatch/schedule-draft': [t('ak_nav_dispatch_room'), t('ak_nav_trading_schedule_mockup')],
    '/dispatch/forecast-mms': [hasArtemKView(getStoredUser()?.login)
      ? t('nav_optimization') : t('nav_forecast_mms')],
    '/financial/result': [t('ak_nav_financial_result')],
    '/budget': [t('ak_nav_budget')],
    '/bess': [t('d_bc_dispatch'), 'BESS'],
    '/market/ueex': [t('d_bc_analytics'), t('d_bc_market')],
    '/info': [t('d_bc_analytics'), t('d_bc_info')],
    '/assets': [t('d_bc_home'), t('d_bc_assets')],
  };
  const crumbs = ROUTE_CRUMBS[location.pathname] ?? breadcrumb;
  const currentUser = getStoredUser();
  const [stationOpen, setStationOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const [assets, setAssets] = useState<AssetItem[]>([]);
  const { selectedAsset, setSelectedAsset } = useSelectedAsset();
  const { isDark, toggleTheme } = useTheme();
  // Спільна з тілом сторінки доба Прогноз/MMS (керується звідси при isFcmms).
  const { date: fcDate, setDate: setFcDate } = useForecastMmsDate();
  const fcTabs = getFcmmsDayTabs();
  const fcDmy = (iso: string) => (iso ? iso.split('-').reverse().join('.') : '');
  // Спільний стан шапки «Фінансовий результат».
  const fin = useFinancialResultUi();
  const [aeBusy, setAeBusy] = useState(false);
  const downloadAnalytics = async () => {
    if (!selectedAsset?.id || aeBusy) return;
    const monthEnd = (m: string) => { const [y, mo] = m.split('-').map(Number); return `${m}-${String(new Date(y, mo, 0).getDate()).padStart(2, '0')}`; };
    const df = fin.period === 'day' ? fin.date : fin.period === 'custom' ? fin.from : `${fin.month}-01`;
    const dt = fin.period === 'day' ? fin.date : fin.period === 'custom' ? fin.to : monthEnd(fin.month);
    setAeBusy(true);
    try {
      const res = await authFetch(`${API_BASE_URL}/forecasting/assets/${encodeURIComponent(selectedAsset.id)}/analytics-export.xlsx?dateFrom=${df}&dateTo=${dt}`);
      if (!res.ok) throw new Error('export failed');
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `helios-analytics-${selectedAsset.assetName}-${df}_${dt}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(a.href);
    } catch { /* silent */ } finally { setAeBusy(false); }
  };
  const canEditFin = ['Оператор', 'CTO', 'Фінансовий директор'].includes(currentUser?.role ?? '');
  // Спільний стан шапки /forecast (редизайн).
  const fcu = useForecastUi();
  const fcTabsRd = getDayTabLabels();
  const sd = useScheduleDraftUi();
  // Спільний стан шапки /market/prices (редизайн).
  const mk = useMarketPricesUi();
  const tr = useTradingUi();
  const cd = useComdispUi();
  // Спільний стан шапки «Бюджету».
  const bu = useBudgetUi();
  // Іконки перемикачів «Бюджету». Підписи НЕ дублюємо — беремо з того самого
  // хука, що й сторінка, інакше вони б із часом розійшлися.
  const BUDGET_TAB_ICON: Record<string, LucideIcon> = {
    month: CalendarDays, year: CalendarRange, accuracy: Target, cash: Wallet,
  };
  const BUDGET_STAGE_ICON: Record<string, LucideIcon> = {
    draft: PencilLine, final: FileCheck,
  };
  const BUDGET_SCENARIO_ICON: Record<string, LucideIcon> = {
    low: TrendingDown, base: Minus, high: TrendingUp,
  };
  /**
   * Сегментована пілюля ЛИШЕ з іконок за стандартом §6 (36 зовні / 28 всередині,
   * контурний сірий active). Підпис — у підказці та aria-label.
   */
  const budgetPills = (
    items: { key: string; label: string; title?: string; Icon: LucideIcon }[],
    active: string,
    onPick: (k: string) => void,
    narrowHidden = false,
  ) => (
    <div className={`${narrowHidden ? 'hidden lg:flex' : 'flex'} items-center gap-0.5 px-1 shrink-0`}
      style={{ height: 36, borderRadius: 10, background: inputBg, border: `1px solid ${inputBorder}`, boxShadow: ctrlSh }}>
      {items.map((it) => {
        const on = active === it.key;
        return (
          <button key={it.key} onClick={() => onPick(it.key)}
            title={it.title || it.label} aria-label={it.title || it.label}
            className="flex items-center justify-center cursor-pointer transition-colors"
            style={{
              width: 28, height: 28, borderRadius: 8,
              background: on ? rdAccentSoft : 'transparent',
              border: `1px solid ${on ? rdAccent : 'transparent'}`,
              color: on ? rdAccent : textMuted,
            }}>
            <it.Icon size={15} strokeWidth={2} />
          </button>
        );
      })}
    </div>
  );

  /** Генерація версії місяця: дію робить хедер (§6), дані перечитує сторінка. */
  const budgetGenerate = async () => {
    if (!selectedAsset?.id || bu.busy) return;
    bu.setBusy('generate');
    try {
      const r = await authFetch(`${API_BASE_URL}/budget/month/generate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assetId: selectedAsset.id, month: bu.month, stage: bu.stage }),
      });
      if (!r.ok) {
        const b = await r.json().catch(() => ({}));
        window.alert(b?.message || t('ak_nav_generation_failed_http', { status: r.status }));
      }
    } catch { window.alert(t('ak_nav_failed_to_generate_the_budget')); }
    finally { bu.setBusy(''); bu.reload(); }
  };
  const budgetExport = async () => {
    if (!selectedAsset?.id) return;
    try {
      const r = await authFetch(
        `${API_BASE_URL}/budget/month/export.xlsx?assetId=${encodeURIComponent(selectedAsset.id)}&month=${bu.month}`);
      if (!r.ok) { window.alert(t('ak_nav_export_failed_http', { status: r.status })); return; }
      const blob = await r.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `helios-budget-${bu.month}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(a.href);
    } catch { window.alert(t('ak_nav_export_failed')); }
  };
  const sko = useSkoUi();
  const st = useSaleTermsUi();
  const as = useAssetsUi();
  const ux = useUeexUi();
  const ct = useContractsUi();
  const us = useUsersUi();
  // Погодинного профілю на майбутнє не існує — ▶ гасне на сьогоднішній добі.
  const trNext = tr.hourlyDate >= tradingTodayIso();
  const mkPrev = !mk.date || (mk.min != null && mk.date <= mk.min);
  const mkNext = !mk.date || (mk.max != null && mk.date >= mk.max);
  // «Ціни» → «За місяць»: замість доби — місяць, стрілки крокують місяцями.
  const mkMonthMode = mk.tab === 'rdn' && mk.period === 'month';
  const mkMonthPrev = !mk.date || (mk.min != null && mk.date.slice(0, 7) <= mk.min.slice(0, 7));
  const mkMonthNext = !mk.date || (mk.max != null && mk.date.slice(0, 7) >= mk.max.slice(0, 7));
  // Кольори й тінь контролів — з того самого hdrColors, що й дрібні контроли
  // дати нижче: два набори тих самих значень роз'їхались би при першій правці.
  const { textMain, textMuted, inputBg, inputBorder, ctrlSh } = hdrColors(isDark);
  // Активний вибір — світло-сірий контур + напівпрозора заливка (без яскравого
  // червоного), щоб зняти кольорове навантаження на цих елементах.
  const rdAccent = isDark ? '#C9CCCE' : '#6E757C';
  const rdAccentSoft = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(40,50,60,0.06)';

  useEffect(() => {
    // Owners (CEO) get only their portfolio via the scoped /owner/stations;
    // other roles keep the full /assets list.
    const isOwner = currentUser?.role === 'CEO';
    const load: Promise<AssetItem[]> = isOwner
      ? authFetch(`${API_BASE_URL}/owner/stations`).then((r) => r.json()).then((d) => (d.stations ?? []) as AssetItem[])
      : api.get<{ data: AssetItem[] } | AssetItem[]>('/assets')
          .then((res) => (Array.isArray(res) ? res : (res as { data: AssetItem[] }).data ?? []));
    load
      .then((list) => {
        setAssets(list);
        const stored = selectedAsset;
        const inScope = stored && list.some((a) => a.id === stored.id);
        // Pick first if nothing stored OR the stored station is outside this account's scope.
        if (list.length > 0 && (!stored || !inScope)) {
          const first = list[0];
          setSelectedAsset({
            id: first.id,
            assetName: first.assetName,
            installedCapacityMw: first.installedCapacityMw,
            region: first.region,
            operationalStatus: first.operationalStatus,
            counterpartyShortName: first.counterpartyShortName,
            counterpartyName: first.counterpartyName,
          });
        }
      })
      .catch(() => {
        setAssets([]);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Картка станції — джерело істини для вибраної СЕС: URL веде селектор, а не
  // навпаки. Інакше після переходу з меню в топбарі лишалась би попередня СЕС.
  useEffect(() => {
    if (!scadaStationId || assets.length === 0) return;
    if (scadaStationIdForAsset(selectedAsset?.id) === scadaStationId) return;
    const match = assets.find((a) => ASSET_TO_MOCK[a.id] === scadaStationId);
    if (!match) return;
    setSelectedAsset({
      id: match.id,
      assetName: match.assetName,
      installedCapacityMw: match.installedCapacityMw,
      region: match.region,
      operationalStatus: match.operationalStatus,
      counterpartyShortName: match.counterpartyShortName,
      counterpartyName: match.counterpartyName,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scadaStationId, assets]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleSelectAsset = (asset: AssetItem) => {
    const mapped: SelectedAsset = {
      id: asset.id,
      assetName: asset.assetName,
      installedCapacityMw: asset.installedCapacityMw,
      region: asset.region,
      operationalStatus: asset.operationalStatus,
      counterpartyShortName: asset.counterpartyShortName,
      counterpartyName: asset.counterpartyName,
    };
    setSelectedAsset(mapped);
    setStationOpen(false);
    // На картці станції вибір СЕС має вести на її ж картку. Маршрут наявний
    // (/scada/station/:id), id беремо з наявної мапи ASSET_TO_MOCK.
    if (scadaStationId) {
      const target = scadaStationIdForAsset(asset.id);
      // Вкладку зберігаємо: зміна СЕС не має скидати «Схему» на «Огляд».
      if (target && target !== scadaStationId) navigate(stationPath(target, scadaRoute?.tab));
    }
    // На картці активу вибір СЕС веде на картку тієї ж СЕС. Вкладку зберігаємо
    // тим самим query-параметром, яким її тримає сама сторінка.
    if (assetDetailId && asset.id !== assetDetailId) {
      const tab = new URLSearchParams(location.search).get('tab');
      navigate(`/assets/${asset.id}${tab ? `?tab=${tab}` : ''}`);
    }
  };

  const userMenuItems = [
    { icon: 'ri-user-line', key: 'topbar_profile' },
    { icon: 'ri-settings-3-line', key: 'topbar_settings' },
    { icon: 'ri-building-line', key: 'topbar_company' },
    { icon: 'ri-team-line', key: 'topbar_team' },
  ];

  // Редизайн: у темній темі топбар одного кольору з сайдбаром (#232B33); у світлій
  // — світлий (тільки сайдбар лишається темним).
  const isRdShell = import.meta.env.VITE_REDESIGN === '1';
  const topbarBg = isRdShell ? (isDark ? '#232B33' : '#FFFFFF') : (isDark ? '#1A1A1A' : '#fff');
  const topbarBorder = isRdShell ? (isDark ? 'rgba(255,255,255,0.06)' : '#E4E5E7') : (isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8');
  const hoverBg = isDark ? 'rgba(255,255,255,0.06)' : '#F5F5F7';
  // Випливаючі списки топбара — напівпрозора скляна панель у токенах кабінету
  // замість непрозорих #1A1A1A / #fff. 82% від --surface-solid плюс розмиття
  // дає скло обох тем і лишає текст читабельним поверх контенту сторінки.
  const dropdownBg = 'color-mix(in srgb, var(--surface-solid) 82%, transparent)';
  const dropdownBorder = 'var(--line-strong)';
  const dropdownHover = 'var(--surface-2)';
  // Спільний стиль плаваючої панелі: скло + контур + тінь зі стандарту.
  const dropdownPanel = {
    background: dropdownBg,
    border: `1px solid ${dropdownBorder}`,
    boxShadow: 'var(--shadow)',
    WebkitBackdropFilter: 'blur(var(--glass-blur))',
    backdropFilter: 'blur(var(--glass-blur))',
  } as const;
  const separatorColor = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const langBorder = isDark ? 'rgba(255,255,255,0.1)' : '#E5E5E8';

  // Перемикачі мови й теми винесено в змінні, бо тепер вони мають два місця:
  // у центрі (звичайні сторінки) і праворуч біля аватара (Прогноз/MMS). Єдине
  // джерело розмітки — щоб дві копії не розʼїхались.
  const langSwitch = (
    <div className="hidden md:flex items-center gap-0.5 px-1"
      style={{ height: 36, borderRadius: 10, border: `1px solid ${langBorder}`, background: inputBg, boxShadow: ctrlSh }}>
      {['UA', 'PL', 'EN'].map((lang) => {
        const active = language === lang;
        return (
          <button
            key={lang}
            onClick={() => onLanguageChange(lang)}
            className="px-2.5 text-[11px] font-semibold cursor-pointer transition-colors whitespace-nowrap flex items-center"
            style={{
              height: 28, borderRadius: 8,
              background: active ? rdAccentSoft : 'transparent',
              border: `1px solid ${active ? rdAccent : 'transparent'}`,
              color: active ? rdAccent : textMuted,
              fontFamily: 'Urbanist, Inter, sans-serif',
            }}
          >
            {lang}
          </button>
        );
      })}
    </div>
  );
  const themeToggle = (
    <button
      onClick={toggleTheme}
      className="w-9 h-9 flex items-center justify-center rounded-md cursor-pointer transition-all"
      style={{
        background: isDark ? 'rgba(255,255,255,0.05)' : '#F5F5F7',
        // Сонце було золотим (#FFD700) — єдина кольорова пляма в шапці, яка
        // ні про що не сигналізувала. Тепер біле: це перемикач, а не статус.
        color: isDark ? '#FFFFFF' : '#6B6B70',
      }}
      title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      onMouseEnter={(e) => { e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.1)' : '#EBEBEE'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.05)' : '#F5F5F7'; }}
    >
      <div className="w-4 h-4 flex items-center justify-center">
        <i className={`text-sm ${isDark ? 'ri-sun-line' : 'ri-moon-line'}`}></i>
      </div>
    </button>
  );

  /**
   * Перемикач ринку в шапці «Цін» — тепер із четвертою вкладкою «Торговий
   * графік», яка веде на /trading.
   *
   * ЧОМУ ВІН МАЛЮЄТЬСЯ Й НА /trading. Вкладка мусить працювати в обидва боки:
   * якби на сторінці торгів перемикача не було, назад до «Цін» вело б лише
   * меню. Тому та сама група стоїть і там — з активною четвертою вкладкою, а
   * перші три повертають на «Ціни» вже з обраним ринком.
   *
   * Лише для ArtemK: у нього «Торговий графік» — частина «Цін», а не окремий
   * розділ. Решта акаунтів бачить звичні три вкладки й окремий пункт меню.
   */
  const marketTabsGroup = (onTrading: boolean) => (
    <div className="flex items-center px-0.5"
      style={{ height: 36, borderRadius: 10, background: inputBg, border: `1px solid ${inputBorder}`, boxShadow: ctrlSh }}>
      {(([['rdn', t('pv2_dam')], ['idm', t('pv2_idm')], ['br_oper', t('pv2_bm')]]) as [MarketTab, string][]).map(([k, lbl]) => {
        // «БР» активна для обох видів (оперативні / фактичні) — вони перемикаються в тілі сторінки.
        const isBr = k === 'br_oper';
        const active = !onTrading && (isBr ? mk.tab === 'br_oper' || mk.tab === 'br_fact' : mk.tab === k);
        return (
          <button key={k}
            onClick={() => { if (!(isBr && (mk.tab === 'br_oper' || mk.tab === 'br_fact'))) mk.setTab(k); if (onTrading) navigate('/market/prices'); }}
            className="flex items-center px-2.5 text-[11px] font-medium cursor-pointer whitespace-nowrap transition-colors"
            style={{ height: 28, borderRadius: 8, background: active ? rdAccentSoft : 'transparent',
                     border: `1px solid ${active ? rdAccent : 'transparent'}`, color: active ? rdAccent : textMuted }}>
            {lbl}
          </button>
        );
      })}
      {isArtemK && (
        <button
          onClick={() => { if (!onTrading) navigate('/trading'); }}
          className="flex items-center px-2.5 text-[11px] font-medium cursor-pointer whitespace-nowrap transition-colors"
          style={{ height: 28, borderRadius: 8, background: onTrading ? rdAccentSoft : 'transparent',
                   border: `1px solid ${onTrading ? rdAccent : 'transparent'}`, color: onTrading ? rdAccent : textMuted }}>
          {t('ak_nav_trading_schedule')}
        </button>
      )}
    </div>
  );

  return (
    <header
      className="fixed top-0 right-0 z-20 flex items-center px-4 md:px-6 h-[72px]"
      style={{
        left: isMobile ? 0 : `${sidebarWidth}px`,
        background: topbarBg,
        borderBottom: `1px solid ${topbarBorder}`,
        transition: 'left 0.3s',
      }}
    >
      {/* Mobile hamburger */}
      {isMobile && (
        <button
          onClick={onMobileMenuToggle}
          className="w-9 h-9 flex items-center justify-center rounded-md cursor-pointer mr-3 shrink-0"
          style={{ color: textMain }}
        >
          <i className="ri-menu-line text-xl"></i>
        </button>
      )}

      {/* LEFT: breadcrumb + station selector.
          На takeover-сторінках БЕЗ flex-1 — зона має природну ширину й лише
          стискається (назва станції обрізається), а не центрується й не налазить
          на центр. На звичайних сторінках лишається flex-1 (центрований пошук). */}
      <div className={`flex items-center gap-2 min-w-0 pr-3 md:pr-5 ${isTakeover ? '' : 'flex-1'}`}>
        {import.meta.env.VITE_STAND === 'test' && (
          <span
            title={t('ak_nav_test_stand_cabinet2_api_and_database_a')}
            className="shrink-0"
            style={{
              background: '#F59E0B', color: '#1A1A1A', fontWeight: 800, fontSize: 11,
              padding: '3px 10px', borderRadius: 999, letterSpacing: '0.5px',
            }}>
            {t('ak_nav_test')}
          </span>
        )}
        {/* ЗОНА НАЗВИ РОЗДІЛУ — фіксованої ширини.
            Раніше блок мав природну ширину, тож «Прогноз» і «Моніторинг
            обладнання» зсували все праворуч на різну відстань, і селектор
            станції стрибав при кожному переході. Тепер зона резервує місце під
            найдовшу назву в кабінеті («Моніторинг обладнання» / «Фінансовий
            результат»), а сам підпис обрізається трикрапкою всередині.
            Ширина стала, НЕ залежить від поточного заголовка — тож роздільник
            і селектор мають однакову координату X на всіх розділах.
            Один опис на весь топбар: жодних per-page відступів.
            У топбарі — назва ЛИШЕ поточного розділу; масив crumbs лишається
            повним, змінено тільки рендер. */}
        <div
          className="hidden sm:grid items-center shrink min-w-0 max-w-full"
          style={{ fontFamily: 'Urbanist, Inter, sans-serif', gridTemplateAreas: '"title"' }}
        >
          {/* Невидимий вимірювач: тримає ширину зони рівно під найдовшу назву
              розділу, де показується селектор. Обидва елементи лежать в одній
              комірці ґрида, тож ширина = ширина вимірювача, без жодного
              підібраного «на око» піксельного значення й без absolute.
              Завдяки цьому селектор стоїть на місці при переходах І водночас
              максимально близько до назви: зайвого резерву немає. */}
          <span
            aria-hidden
            className="invisible text-sm font-medium whitespace-nowrap"
            style={{ gridArea: 'title' }}
          >
            {LONGEST_SECTION_TITLE}
          </span>
          <span className="min-w-0" style={{ gridArea: 'title' }}>
            {isFinResult ? (
              /* Фінансовий результат: заголовок у рядок, W-код дрібним під ним. */
              <span className="flex flex-col justify-center leading-tight min-w-0">
                <span className="text-sm font-medium truncate" style={{ color: textMain }}>
                  {t('ak_nav_financial_result')}
                </span>
                {fin.wCode && (
                  <span className="truncate" style={{ color: textMuted, fontSize: 10, letterSpacing: '0.2px' }}>W: {fin.wCode}</span>
                )}
              </span>
            ) : crumbs.length > 0 ? (
              <span className="block text-sm font-medium truncate" style={{ color: textMain }}>
                {crumbs[crumbs.length - 1]}
              </span>
            ) : null}
          </span>
        </div>
        {/* Роздільник залежить ЛИШЕ від наявності селектора (див. вище) і НЕ
            має власних горизонтальних відступів: проміжок дає gap-2 самої
            лівої зони. Раніше mx-3 додавався ДО gap-2, і між назвою та
            селектором набігало ~41px порожнечі. Тепер 8 + лінія + 8. */}
        {!hideStationSelector && <div className="hidden sm:block h-4 shrink-0" style={{ borderLeft: `1px solid ${separatorColor}` }}></div>}

        {/* Station / Asset selector — hidden in the owner cabinet (non-functional there) */}
        {!hideStationSelector && (
        <div className="relative min-w-0">
          <button
            onClick={() => setStationOpen(!stationOpen)}
            className={`flex items-center gap-2 px-2 md:px-3 text-sm cursor-pointer transition-colors whitespace-nowrap ${stationPill ? 'min-w-0' : ''}`}
            style={{
              // ШИРИНА СТАЛА. Раніше кнопка росла за назвою, і сусідні контроли
              // стрибали при кожному перемиканні СЕС: «СЕС ЛУЧКИ» проти
              // «СЕС НІКОПОЛЬ-ЕЛІОС» — різниця понад 90px. Тепер назва
              // обрізається трикрапкою в межах сталої ширини, а повна стоїть у
              // підказці. На takeover-сторінках вужче: там у лівій зоні лише
              // назва, без потужності.
              width: stationPill ? 210 : 260,
              // На takeover-сторінках — заповнена пілюля 32px зі скругленням 10 і тінню,
              // єдина з рештою контролів; на звичайних — попередній вигляд.
              height: stationPill ? 36 : undefined,
              paddingTop: stationPill ? 0 : 6, paddingBottom: stationPill ? 0 : 6,
              borderRadius: stationPill ? 10 : 6,
              background: stationPill ? inputBg : (stationOpen ? hoverBg : 'transparent'),
              border: `1px solid ${inputBorder}`,
              boxShadow: stationPill ? ctrlSh : undefined,
              color: textMain,
              fontFamily: 'Urbanist, Inter, sans-serif',
            }}
          >
            <div
              className="w-2 h-2 rounded-full shrink-0"
              style={{ background: selectedAsset?.operationalStatus === 'active' ? '#0D7A3C' : '#6B6B70' }}
            ></div>
            {/* Назва займає все вільне місце й обрізається трикрапкою — так
                кнопка тримає сталу ширину незалежно від довжини назви (СЕС
                НІКОПОЛЬ-ЕЛІОС, СЕС ПРОМЕТЕЙ ЕТГ). Повна назва — у підказці. */}
            <span className="font-medium truncate flex-1 min-w-0 text-left"
              title={selectedAsset?.assetName}>
              {selectedAsset ? selectedAsset.assetName : '—'}
            </span>
            {/* Потужність приховано на Прогноз/MMS — там у лівій зоні лише назва */}
            {selectedAsset && !stationPill && (
              <span className="hidden sm:inline shrink-0" style={{ color: textMuted }}>· {selectedAsset.installedCapacityMw} {t('ak_nav_mw')}</span>
            )}
            <i className="ri-arrow-down-s-line text-sm shrink-0" style={{ color: textMuted }}></i>
          </button>

          {stationOpen && (
            <div
              className="absolute top-full mt-1 left-0 rounded-lg z-50 py-1 min-w-[240px]"
              style={dropdownPanel}
            >
              {assets.length === 0 ? (
                <div className="px-4 py-3 text-sm" style={{ color: textMuted, fontFamily: 'Urbanist, Inter, sans-serif' }}>
                  <i className="ri-flashlight-line mr-2"></i>
                  No assets found
                </div>
              ) : (
                assets.map((asset) => (
                  <button
                    key={asset.id}
                    onClick={() => handleSelectAsset(asset)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left cursor-pointer transition-colors"
                    style={{ fontFamily: 'Urbanist, Inter, sans-serif', color: textMain }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = dropdownHover; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <div
                      className="w-2 h-2 rounded-full shrink-0"
                      style={{ background: asset.operationalStatus === 'active' ? '#0D7A3C' : '#6B6B70' }}
                    ></div>
                    <div>
                      <div className="font-medium">{asset.assetName}</div>
                      <div className="text-xs" style={{ color: textMuted }}>
                        {asset.installedCapacityMw} {t('ak_nav_mw')} · {localizeRegion(asset.region, language)}
                      </div>
                    </div>
                    {selectedAsset?.id === asset.id && (
                      <i className="ri-check-line ml-auto text-sm" style={{ color: '#0D7A3C' }}></i>
                    )}
                  </button>
                ))
              )}
              <div style={{ borderTop: `1px solid ${separatorColor}` }} className="mt-1 pt-1">
                <button
                  onClick={() => { navigate('/assets'); setStationOpen(false); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left cursor-pointer transition-colors"
                  style={{ color: 'var(--accent)', fontFamily: 'Urbanist, Inter, sans-serif' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = dropdownHover; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <i className="ri-add-line text-sm"></i>
                  {t('topbar_add_station')}
                </button>
              </div>
            </div>
          )}
        </div>
        )}
      </div>

      {/* CENTER: пошук (звичайні сторінки) АБО керування добою (Прогноз/MMS).
          shrink-0 — центр має природну ширину й НЕ стискається, тож довга назва
          станції зліва не може на нього налізти. Між двома рівними flex-1
          зонами (ліворуч і праворуч) він однаково лишається строго по центру.
          Мови й тему звідси прибрано (вони йдуть праворуч). */}
      <div className="flex items-center shrink-0">
      {/* Пошук прибрано з топбара повністю (нефункціональний). */}

      {/* Прогноз/MMS: швидкі дні + ◀ дата ▶ — керування добою на виду при скролі.
          Вкладки D-2…D+2 ховаються на вузькому екрані, дата-навігація лишається. */}
      {isFcmms && (
        <div className="flex items-center gap-2">
          {/* Швидкі дні D-2…D+2 — сегментований контрол (36px, radius 10, ctrlSh),
              єдиний стиль з /forecast · РДН · Фінрезультат. */}
          <div className="hidden lg:flex items-center px-0.5"
            style={{ height: 36, borderRadius: 10, background: inputBg, border: `1px solid ${inputBorder}`, boxShadow: ctrlSh }}>
            {fcTabs.map((d) => {
              const active = fcDate === d.apiDate;
              return (
                <button key={d.apiDate} onClick={() => setFcDate(d.apiDate)}
                  className="flex items-center px-2.5 text-[11px] font-medium cursor-pointer whitespace-nowrap transition-colors"
                  style={{ height: 28, borderRadius: 8, background: active ? rdAccentSoft : 'transparent',
                           border: `1px solid ${active ? rdAccent : 'transparent'}`, color: active ? rdAccent : textMuted }}>
                  {d.label === 'Today' ? t('ak_nav_today') : d.label}
                </button>
              );
            })}
          </div>
          <HdrDateField value={fcDate} onChange={setFcDate} title={fcDmy(fcDate)} />
          <HdrStep dir="prev" onClick={() => setFcDate(shiftDay(fcDate, -1))} title={t('ak_nav_previous_day')} />
          <HdrStep dir="next" onClick={() => setFcDate(shiftDay(fcDate, 1))} title={t('ak_nav_next_day')} />
        </div>
      )}

      {/* Фінансовий результат: період · дата/місяць · «Факт. розрахунок» — на виду при скролі. */}
      {isFinResult && (
        <div className="flex items-center gap-2">
          {/* Період: День/Місяць/Період — контурний сірий active (rdAccent), без червоного; 36px як на /forecast */}
          <div className="flex items-center px-0.5"
            style={{ height: 36, borderRadius: 10, background: inputBg, border: `1px solid ${inputBorder}`, boxShadow: ctrlSh }}>
            {(['day', 'month', 'custom'] as const).map((p) => {
              const active = fin.period === p;
              return (
                <button key={p} onClick={() => fin.setPeriod(p)}
                  className="flex items-center px-2.5 text-[11px] font-medium cursor-pointer whitespace-nowrap transition-colors"
                  style={{ height: 28, borderRadius: 8, background: active ? rdAccentSoft : 'transparent',
                           border: `1px solid ${active ? rdAccent : 'transparent'}`, color: active ? rdAccent : textMuted }}>
                  {p === 'day' ? t('ak_nav_day') : p === 'month' ? t('ak_nav_month') : t('ak_nav_period')}
                </button>
              );
            })}
          </div>
          {fin.period === 'custom' ? (
            // Довільний період — два поля без стрілок: крок на добу тут
            // безглуздий, бо невідомо, яку з двох меж він мав би рухати.
            <div className="flex items-center gap-1.5">
              <HdrDateField value={fin.from} max={fin.to || undefined} onChange={fin.setFrom} title={t('ak_nav_period_start')} />
              <span style={{ color: textMuted, fontSize: 12 }}>—</span>
              <HdrDateField value={fin.to} min={fin.from || undefined} onChange={fin.setTo} title={t('ak_nav_period_end')} />
            </div>
          ) : (
          <div className="flex items-center gap-1.5">
            {/* Поле доби й поле місяця — однакової ширини 180, щоб стрілки
                стояли на тому самому місці при перемиканні періоду. */}
            {fin.period === 'day' ? (
              <HdrDateField value={fin.date} onChange={fin.setDate} width={180} />
            ) : (
              <HdrMonthField value={fin.month} onChange={fin.setMonth} />
            )}
            <HdrStep
              dir="prev"
              onClick={() => (fin.period === 'day' ? fin.setDate(shiftDay(fin.date, -1)) : fin.setMonth(shiftMonth(fin.month, -1)))}
              title={t('ak_nav_previous_period')}
            />
            <HdrStep
              dir="next"
              onClick={() => (fin.period === 'day' ? fin.setDate(shiftDay(fin.date, 1)) : fin.setMonth(shiftMonth(fin.month, 1)))}
              title={t('ak_nav_next_period')}
            />
          </div>
          )}
          {fin.period === 'day' && canEditFin && (
            <button onClick={() => fin.requestEntry()}
              className="flex items-center justify-center cursor-pointer shrink-0"
              style={{ width: 36, height: 36, borderRadius: 10, background: inputBg, border: `1px solid ${inputBorder}`, color: textMain, boxShadow: ctrlSh }}
              title={t('ak_nav_actual_settlement_enter_the_actual_set')}>
              <Calculator size={16} strokeWidth={1.9} />
            </button>
          )}
          {selectedAsset && (
            <button onClick={downloadAnalytics} disabled={aeBusy}
              className="flex items-center justify-center cursor-pointer shrink-0"
              style={{ width: 36, height: 36, borderRadius: 10, background: inputBg, border: `1px solid ${inputBorder}`, color: textMain, boxShadow: ctrlSh, opacity: aeBusy ? 0.6 : 1 }}
              title={t('ak_nav_analytics_export_ae_xlsx_base_ecmwf_fo')}>
              <FileSpreadsheet size={16} strokeWidth={1.9} />
            </button>
          )}
        </div>
      )}

      {/* /forecast (редизайн): вкладки дат · ◀дата▶ · Експорт (іконка-квадрат). */}
      {isForecastRd && (
        <div className="flex items-center gap-2">
          {/* Усі контроли єдиної висоти 32, скруглення 10, з тінню — один стиль. */}
          <div className="hidden lg:flex items-center gap-0.5 px-1"
            style={{ height: 36, borderRadius: 10, background: inputBg, border: `1px solid ${inputBorder}`, boxShadow: ctrlSh }}>
            {fcTabsRd.map((d) => {
              const active = fcu.date === d.apiDate;
              return (
                <button key={d.apiDate} onClick={() => fcu.setDate(d.apiDate)}
                  className="px-2.5 text-[11px] font-medium cursor-pointer whitespace-nowrap transition-colors flex items-center"
                  style={{ height: 28, borderRadius: 8, background: active ? rdAccentSoft : 'transparent', border: `1px solid ${active ? rdAccent : 'transparent'}`, color: active ? rdAccent : textMuted }}>
                  {d.label === 'Today' ? t('ak_nav_today') : d.label}
                </button>
              );
            })}
          </div>
          <HdrDateField value={fcu.date} onChange={fcu.setDate} />
          <HdrStep dir="prev" onClick={() => fcu.setDate(shiftDayIso(fcu.date, -1))} title={t('ak_nav_previous_day')} />
          <HdrStep dir="next" onClick={() => fcu.setDate(shiftDayIso(fcu.date, 1))} title={t('ak_nav_next_day')} />
          <button onClick={() => fcu.requestExport()}
            className="flex items-center justify-center cursor-pointer shrink-0"
            style={{ width: 36, height: 36, borderRadius: 10, background: inputBg, border: `1px solid ${inputBorder}`, color: textMain, boxShadow: ctrlSh }} title={t('ak_nav_export_xlsx')}>
            <i className="ri-download-line" style={{ fontSize: 16 }} />
          </button>
        </div>
      )}

      {/* Макет торгового графіка: D+1 / D+2 · ◀дата▶. Ті самі контроли 36/10,
          що на «Прогнозі»: сірий контурний active, без брендового червоного. */}
      {isScheduleDraft && (
        <div className="flex items-center gap-2">
          <div className="hidden sm:flex items-center gap-0.5 px-1"
            style={{ height: 36, borderRadius: 10, background: inputBg, border: `1px solid ${inputBorder}`, boxShadow: ctrlSh }}>
            {(['d1', 'd2'] as const).map((h) => {
              const active = sd.horizon === h;
              return (
                <button key={h} onClick={() => sd.setHorizon(h)}
                  className="px-2.5 text-[11px] font-medium cursor-pointer whitespace-nowrap transition-colors flex items-center"
                  style={{ height: 28, borderRadius: 8, background: active ? rdAccentSoft : 'transparent',
                           border: `1px solid ${active ? rdAccent : 'transparent'}`, color: active ? rdAccent : textMuted }}>
                  {h === 'd1' ? 'D+1' : 'D+2'}
                </button>
              );
            })}
          </div>
          <HdrDateField value={sd.date} onChange={sd.setDate} title={t('ak_nav_schedule_submission_day')} />
          <HdrStep dir="prev" onClick={() => sd.setDate(shiftDraftDay(sd.date, -1))} title={t('ak_nav_previous_day')} />
          <HdrStep dir="next" onClick={() => sd.setDate(shiftDraftDay(sd.date, 1))} title={t('ak_nav_next_day')} />
        </div>
      )}

      {/* Ринок, ціни (редизайн): вкладка ринку · ◀дата▶ · Графік/Таблиця. */}
      {isMarketPrices && (
        <div className="flex items-center gap-2">
          {/* Вкладка ринку — сегментований контрол, контурний сірий active (без червоного). */}
          {marketTabsGroup(false)}
          {/* Швидкі дні D−2…D+2 (той самий контрол, що на /forecast); у режимі «Місяць» не потрібні. */}
          {!mkMonthMode && (
            <div className="hidden lg:flex items-center px-0.5"
              style={{ height: 36, borderRadius: 10, background: inputBg, border: `1px solid ${inputBorder}`, boxShadow: ctrlSh }}>
              {fcTabsRd.map((d) => {
                const active = mk.date === d.apiDate;
                return (
                  <button key={d.apiDate} onClick={() => mk.setDate(d.apiDate)}
                    className="flex items-center px-2.5 text-[11px] font-medium cursor-pointer whitespace-nowrap transition-colors"
                    style={{ height: 28, borderRadius: 8, background: active ? rdAccentSoft : 'transparent',
                             border: `1px solid ${active ? rdAccent : 'transparent'}`, color: active ? rdAccent : textMuted }}>
                    {d.label === 'Today' ? t('ak_nav_today') : d.label}
                  </button>
                );
              })}
            </div>
          )}
          {/* Дата (або місяць), праворуч кроки — стрілки гаснуть на межах наявних торгів і прогнозу. */}
          {mkMonthMode ? (
            <>
              <HdrMonthField value={mk.date.slice(0, 7)} min={mk.min?.slice(0, 7)} max={mk.max?.slice(0, 7)} onChange={mk.setMonth} />
              <HdrStep dir="prev" disabled={mkMonthPrev} onClick={() => mk.setMonth(shiftMonth(mk.date.slice(0, 7), -1))} title={t('ak_nav_previous_month')} />
              <HdrStep dir="next" disabled={mkMonthNext} onClick={() => mk.setMonth(shiftMonth(mk.date.slice(0, 7), 1))} title={t('ak_nav_next_month')} />
            </>
          ) : (
            <>
              <HdrDateField value={mk.date} min={mk.min || undefined} max={mk.max || undefined} onChange={mk.setDate} />
              <HdrStep dir="prev" disabled={mkPrev} onClick={() => mk.setDate(shiftMkDay(mk.date, -1))} title={t('ak_nav_previous_day')} />
              <HdrStep dir="next" disabled={mkNext} onClick={() => mk.setDate(shiftMkDay(mk.date, 1))} title={t('ak_nav_next_day')} />
            </>
          )}
          {/* Графік / Таблиця — у тілі сторінки (між плитками й графіком), як у макеті /tg. */}
        </div>
      )}

      {isBudget && (
        <div className="flex items-center gap-2 shrink-0">
          {/* Вкладка розділу. */}
          {budgetPills(
            BUDGET_TABS.map(([k, lbl]) => ({ key: k, label: lbl, Icon: BUDGET_TAB_ICON[k] })),
            bu.tab, (k) => bu.setTab(k as BudgetTab))}

          {/* Етап регламенту, генерація та експорт — лише на вкладці «Місяць»:
              двигун рахує місяць, і на інших вкладках ці кнопки були б мертві. */}
          {bu.tab === 'month' && (
            <>
              {budgetPills(
                BUDGET_STAGES.map(([k, lbl]) => ({
                  key: k, label: lbl,
                  title: k === 'draft' ? t('ak_nav_draft_t_5_for_approval') : t('ak_nav_final_version_t_1'),
                  Icon: BUDGET_STAGE_ICON[k],
                })),
                bu.stage, (k) => bu.setStage(k as BudgetStage), true)}
              <button onClick={budgetGenerate} disabled={!!bu.busy || !selectedAsset?.id}
                className="flex items-center justify-center shrink-0"
                style={{ width: 36, height: 36, borderRadius: 10, background: inputBg,
                         border: `1px solid ${inputBorder}`, color: textMain, boxShadow: ctrlSh,
                         opacity: bu.busy ? 0.5 : 1, cursor: bu.busy ? 'default' : 'pointer' }}
                title={t('ak_nav_generate_monthly_budget', { stage: bu.stage === 'draft' ? t('ak_nav_draft') : t('ak_nav_final') })}
                aria-label={t('ak_nav_generate_monthly_budget_2')}>
                <Calculator size={17} strokeWidth={2} />
              </button>
              <button onClick={budgetExport} disabled={!selectedAsset?.id}
                className="flex items-center justify-center shrink-0"
                style={{ width: 36, height: 36, borderRadius: 10, background: inputBg,
                         border: `1px solid ${inputBorder}`, color: textMain, boxShadow: ctrlSh }}
                title={t('ak_nav_export_monthly_budget_to_xlsx')} aria-label={t('ak_nav_export_budget')}>
                <FileSpreadsheet size={17} strokeWidth={2} />
              </button>
            </>
          )}

          {/* Місяць — ◀ поле ▶. На вкладці «Рік» місяць не має сенсу. */}
          {bu.tab !== 'year' && (
            <>
              <HdrMonthField value={bu.month} onChange={bu.setMonth} />
              <HdrStep dir="prev" onClick={() => bu.setMonth(shiftMonth(bu.month, -1))} title={t('ak_nav_previous_month')} />
              <HdrStep dir="next" onClick={() => bu.setMonth(shiftMonth(bu.month, 1))} title={t('ak_nav_next_month')} />
            </>
          )}

          {/* Сценарій цін: спад / без зміни / зростання — значення самоочевидне
              зі значка, повні підписи лишились у підказці. */}
          {budgetPills(
            BUDGET_SCENARIOS.map(([k, lbl]) => ({
              key: k, label: lbl, title: t('ak_nav_price_scenario', { s: lbl.toLowerCase() }),
              Icon: BUDGET_SCENARIO_ICON[k],
            })),
            bu.scenario, (k) => bu.setScenario(k as BudgetScenario), true)}
        </div>
      )}

      {isTrading && (
        <div className="flex items-center gap-2">
          {/* Той самий перемикач ринку, що на «Цінах»: звідси до них і назад. */}
          {isArtemK && marketTabsGroup(true)}
          {/* Глибина історії — сегментована пілюля за стандартом 36/28. */}
          <div className="flex items-center gap-0.5 px-1"
            style={{ height: 36, borderRadius: 10, background: inputBg, border: `1px solid ${inputBorder}`, boxShadow: ctrlSh }}>
            {(([7, 14, 30, 60]) as TradingDays[]).map((d) => {
              const active = tr.days === d;
              return (
                <button key={d} onClick={() => tr.setDays(d)}
                  className="flex items-center px-2.5 text-[11px] font-medium cursor-pointer whitespace-nowrap transition-colors"
                  style={{ height: 28, borderRadius: 8, background: active ? rdAccentSoft : 'transparent',
                           border: `1px solid ${active ? rdAccent : 'transparent'}`, color: active ? rdAccent : textMuted }}>
                  {d}{t('ak_nav_d')}
                </button>
              );
            })}
          </div>
          {/* Дата, праворуч кроки — та сама трійка, що на /forecast. */}
          <HdrDateField value={tr.hourlyDate} max={tradingTodayIso()} onChange={tr.setHourlyDate} />
          <HdrStep dir="prev" onClick={() => tr.setHourlyDate(shiftDayIso(tr.hourlyDate, -1))} title={t('ak_nav_previous_day')} />
          <HdrStep dir="next" disabled={trNext} onClick={() => tr.setHourlyDate(shiftDayIso(tr.hourlyDate, 1))} title={t('ak_nav_next_day')} />
        </div>
      )}

      {isComdisp && (
        <div className="flex items-center gap-2">
          {/* Доба поставки: поле, праворуч кроки — як на /forecast і /trading. */}
          <HdrDateField value={cd.date} onChange={cd.setDate} title={t('ak_nav_delivery_day')} />
          <HdrStep dir="prev" onClick={() => cd.setDate(shiftComdispDay(cd.date, -1))} title={t('ak_nav_previous_day')} />
          <HdrStep dir="next" onClick={() => cd.setDate(shiftComdispDay(cd.date, 1))} title={t('ak_nav_next_day')} />
        </div>
      )}

      {isSko && (
        <div className="flex items-center min-w-0">
          {/* Секції — сегментована пілюля за стандартом 36/28, як вкладки ринку. */}
          <div className="flex items-center gap-0.5 px-1 overflow-x-auto"
            style={{ height: 36, borderRadius: 10, background: inputBg, border: `1px solid ${inputBorder}`, boxShadow: ctrlSh }}>
            {(([['inbox', t('sko_section_inbox')], ['prs', t('sko_section_prs')],
               ['curtailment', t('sko_section_curtailment')], ['about', t('sko_section_about')]]) as [SkoSection, string][]).map(([k, lbl]) => {
              const active = sko.section === k;
              return (
                <button key={k} onClick={() => sko.setSection(k)}
                  className="px-2.5 text-[11px] font-medium cursor-pointer whitespace-nowrap transition-colors flex items-center shrink-0"
                  style={{ height: 28, borderRadius: 8, background: active ? rdAccentSoft : 'transparent',
                           border: `1px solid ${active ? rdAccent : 'transparent'}`, color: active ? rdAccent : textMuted }}>
                  {lbl}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {isSaleTerms && (
        <div className="flex items-center gap-2 min-w-0">
          <TokenSelect
            value={st.asset} onChange={st.setAsset} placeholder="Усі об’єкти"
            ariaLabel="Фільтр за об’єктом" width={210} maxWidth={210}
            triggerStyle={{ background: inputBg, borderColor: inputBorder, boxShadow: ctrlSh, color: textMain }}
            options={[{ value: '', label: 'Усі об’єкти' },
                      ...st.assets.map((a) => ({ value: a.id, label: a.assetName }))]} />
          <TokenSelect
            value={st.status} onChange={st.setStatus} placeholder="Усі статуси"
            ariaLabel="Фільтр за статусом" width={150}
            triggerStyle={{ background: inputBg, borderColor: inputBorder, boxShadow: ctrlSh, color: textMain }}
            options={[{ value: '', label: 'Усі статуси' },
                      ...SALE_TERM_STATUSES.map((x) => ({ value: x.value, label: x.label }))]} />
        </div>
      )}

      {isAssets && (
        <div className="flex items-center gap-2 min-w-0">
          <div className="flex items-center gap-2 px-2.5 min-w-0"
            style={{ height: 36, borderRadius: 10, background: inputBg, border: `1px solid ${inputBorder}`,
                     boxShadow: ctrlSh, width: 250 }}>
            <Search size={14} strokeWidth={2} style={{ color: textMuted, flexShrink: 0 }} />
            <input value={as.search} onChange={(e) => as.setSearch(e.target.value)}
              placeholder={t('assets_search')} aria-label={t('assets_search')}
              className="bg-transparent outline-none text-[12.5px] min-w-0 flex-1"
              style={{ color: textMain }} />
          </div>
          <TokenSelect
            value={as.type} onChange={as.setType} width={175}
            ariaLabel={t('assets_all_types')} placeholder={t('assets_all_types')}
            triggerStyle={{ background: inputBg, borderColor: inputBorder, boxShadow: ctrlSh, color: textMain }}
            options={[{ value: '', label: t('assets_all_types') },
                      ...ASSET_TYPES.map((v) => ({ value: v, label: t(`asset_type_${v}`) }))]} />
        </div>
      )}

      {isUeex && (
        <div className="flex items-center gap-2 min-w-0">
          <SegmentedTabs
            items={([['electric_power', 'ueex_section_electric_power'],
                     ['bilateral_contracts', 'ueex_section_bilateral_contracts'],
                     ['solar_go', 'ueex_section_solar_go'],
                     ['renewable_go', 'ueex_section_renewable_go'],
                     ['auctions', 'ueex_section_auctions']] as [UeexSection, string][])
                     .map(([k, key]) => ({ key: k, label: t(key) }))}
            activeKey={ux.section}
            onSelect={(k) => ux.setSection(k as UeexSection)}
          />
          <TokenSelect
            value={String(ux.days)} onChange={(v) => ux.setDays(parseInt(v, 10))}
            ariaLabel={t('ueex_page_window')} width={128}
            triggerStyle={{ background: inputBg, borderColor: inputBorder, boxShadow: ctrlSh, color: textMain }}
            options={UEEX_DAYS.map((d) => ({ value: String(d), label: `${d} ${t('ueex_page_days')}` }))} />
        </div>
      )}

      {isContracts && (
        <div className="flex items-center gap-2 px-2.5 min-w-0"
          style={{ height: 36, borderRadius: 10, background: inputBg, border: `1px solid ${inputBorder}`,
                   boxShadow: ctrlSh, width: 280 }}>
          <Search size={14} strokeWidth={2} style={{ color: textMuted, flexShrink: 0 }} />
          <input value={ct.search} onChange={(e) => ct.setSearch(e.target.value)}
            placeholder={t('contracts_search')} aria-label={t('contracts_search')}
            className="bg-transparent outline-none text-[12.5px] min-w-0 flex-1"
            style={{ color: textMain }} />
        </div>
      )}

      {isUsers && (
        <div className="flex items-center gap-2 min-w-0">
          <TokenSelect
            value={us.tenantId} onChange={us.setTenantId} width={200} maxWidth={200}
            ariaLabel={t('users_tenants')} placeholder={t('users_tenants')}
            triggerStyle={{ background: inputBg, borderColor: inputBorder, boxShadow: ctrlSh, color: textMain }}
            options={us.tenants.map((x) => ({ value: x.id, label: x.name }))} />
          <div className="flex items-center gap-2 px-2.5 min-w-0"
            style={{ height: 36, borderRadius: 10, background: inputBg, border: `1px solid ${inputBorder}`,
                     boxShadow: ctrlSh, width: 250 }}>
            <Search size={14} strokeWidth={2} style={{ color: textMuted, flexShrink: 0 }} />
            <input value={us.search} onChange={(e) => us.setSearch(e.target.value)}
              placeholder={t('users_search')} aria-label={t('users_search')}
              className="bg-transparent outline-none text-[12.5px] min-w-0 flex-1"
              style={{ color: textMain }} />
          </div>
        </div>
      )}

      </div>

      {/* RIGHT: мова + тема + аватар. Склад ОДНАКОВИЙ на всіх сторінках.
          Було: СКО-бейдж і дзвіночок сповіщень рендерились під `!isTakeover`, а
          перемикач мови жив у ДВОХ місцях — у центрі на звичайних сторінках і
          праворуч на takeover. Через це шапка мала два різні вигляди залежно
          від розділу. Тепер обидві службові іконки прибрано, а мова описана
          один раз (змінна langSwitch) і завжди стоїть перед аватаром. */}
      <div className={`flex items-center ${isTakeover ? 'ml-auto shrink-0' : 'justify-end flex-1'}`}>
      <div className="flex items-center gap-3 mr-3">
        {langSwitch}
        {themeToggle}
      </div>

      {/* User avatar */}
      <div className="relative">
        <button
          onClick={() => { setUserOpen(!userOpen); setStationOpen(false); }}
          className="flex items-center gap-2 px-2 py-1 rounded-lg cursor-pointer transition-colors"
          style={{ background: userOpen ? hoverBg : 'transparent' }}
          onMouseEnter={(e) => { e.currentTarget.style.background = hoverBg; }}
          onMouseLeave={(e) => { if (!userOpen) e.currentTarget.style.background = 'transparent'; }}
        >
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold"
            style={{ background: 'var(--accent)' }}
          >
            {currentUser?.avatar ?? 'US'}
          </div>
          {/* Єдиний компактний вигляд користувача на ВСІХ маршрутах: лише
              кружок з ініціалами. Раніше ім'я, роль і стрілка були під
              {!isTakeover}, тож на takeover-сторінках блок був компактним, а на
              решті — розгорнутим; звідси й різниця між вкладками. Умову знято,
              а не «підтиснуто» стилями: розмітка тепер одна для всіх.
              Повне ім'я, email, пункти меню й вихід лишились у випадайці нижче. */}
        </button>
        {userOpen && (
          <div
            className="absolute top-full right-0 mt-2 w-56 rounded-xl z-50 overflow-hidden"
            style={dropdownPanel}
          >
            <div className="px-4 py-3" style={{ borderBottom: `1px solid ${separatorColor}` }}>
              <div className="text-sm font-semibold" style={{ fontFamily: 'Urbanist, Inter, sans-serif', color: textMain }}>
                {currentUser?.displayName ?? t('user_guest')}
              </div>
              <div className="text-xs" style={{ color: textMuted }}>{currentUser?.email ?? ''}</div>
            </div>
            {userMenuItems.map((item) => (
              <button
                key={item.key}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left cursor-pointer transition-colors"
                style={{ fontFamily: 'Urbanist, Inter, sans-serif', color: textMain }}
                onMouseEnter={(e) => { e.currentTarget.style.background = dropdownHover; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <i className={`${item.icon} text-sm`} style={{ color: textMuted }}></i>
                {t(item.key)}
              </button>
            ))}
            <div style={{ borderTop: `1px solid ${separatorColor}` }} className="mt-1">
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left cursor-pointer transition-colors whitespace-nowrap"
                style={{ color: 'var(--accent)', fontFamily: 'Urbanist, Inter, sans-serif' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = dropdownHover; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <i className="ri-logout-box-r-line text-sm"></i>
                {t('topbar_logout')}
              </button>
            </div>
          </div>
        )}
      </div>
      </div>
    </header>
  );
}